import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Meta } from '@angular/platform-browser';

import { BehaviorSubject, Observable, Subject } from 'rxjs';

import { isFunction, isPresent, noop } from '@fcom/core/utils/utils';

import { WindowRef } from '../../providers/window';
import { StorageService } from '../storage/storage.service';
import { ConfigService } from '../config/config.service';
import {
  CheckInResult,
  CheckInStatus,
  NativeAppPlatform,
  NativeBridgeService,
  OrderDataForNative,
} from './native-bridge.service';

const NATIVE_KEY = 'X-Native';

interface RequiredPostMessageFunc<T = string> {
  postMessage: (t: T) => void;
}
interface OptionalPostMessageFunc<T = string> {
  postMessage: (t?: T) => void;
}
type PostMessageFunc<T> = RequiredPostMessageFunc<T> | OptionalPostMessageFunc<T>;

type ReactNativeBridge = {
  postMessage: (message: string) => void;
};

interface NativeIosApp {
  addBooking?: RequiredPostMessageFunc<OrderDataForNative>;
  bookingFlowInitialized?: OptionalPostMessageFunc;
  openMenu?: OptionalPostMessageFunc;
  isNativeFinnairIosApp?: boolean;
  onCheckInLoaded?: RequiredPostMessageFunc<CheckInStatus>;
  onAcceptCheckIn?: RequiredPostMessageFunc<CheckInResult>;
}

interface NativeAndroidApp {
  addBooking?: (str: string) => void;
  bookingFlowInitialized?: () => void;
  openMenu?: () => void;
  showMenu?: () => void;
  onCheckInLoaded?: (checkInStatusStr: string) => void;
  onAcceptCheckIn?: (checkInResultStr: string) => void;
}

const sendFn = (windowRef: WindowRef, device: string) => (message: string) =>
  windowRef.nativeWindow?.parent?.postMessage(`[APP_EVENT]: ${device} ${message}`, '*');

const getEmulatedAndroidRef = (windowRef: WindowRef): NativeAndroidApp => {
  const sendMessage = sendFn(windowRef, 'Android');
  return {
    addBooking: (str: string) => sendMessage(`addBooking ${str}`),
    bookingFlowInitialized: () => sendMessage('bookingFlowInitialized'),
    openMenu: () => sendMessage('openMenu'),
    showMenu: () => sendMessage('showMenu'),
    onCheckInLoaded: (checkInStatusStr: string) => sendMessage(`onCheckInLoaded ${checkInStatusStr}`),
    onAcceptCheckIn: (checkInResultStr: string) => sendMessage(`onAcceptCheckIn ${checkInResultStr}`),
  };
};

const getEmulatedIosRef = (windowRef: WindowRef) => {
  const sendMessage = sendFn(windowRef, 'iOS');
  return {
    messageHandlers: {
      isNativeFinnairIosApp: true,
      addBooking: { postMessage: (data: OrderDataForNative) => sendMessage(`addBooking ${JSON.stringify(data)}`) },
      bookingFlowInitialized: { postMessage: () => sendMessage('bookingFlowInitialized') },
      openMenu: { postMessage: () => sendMessage('openMenu') },
      onCheckInLoaded: { postMessage: (data: CheckInStatus) => sendMessage(`onCheckInLoaded ${JSON.stringify(data)}`) },
      onAcceptCheckIn: { postMessage: (data: CheckInResult) => sendMessage(`onAcceptCheckIn ${JSON.stringify(data)}`) },
    } as NativeIosApp,
  };
};

const getEmulatedReactNativeBridge = (windowRef: WindowRef, deviceLabel: string): ReactNativeBridge => {
  const sendMessage = sendFn(windowRef, deviceLabel);
  return {
    postMessage: (rawMessage: string) => {
      sendMessage(rawMessage);
    },
  };
};

@Injectable()
export class ClientNativeBridgeService implements NativeBridgeService {
  private windowRef = inject(WindowRef);
  private meta = inject(Meta);

  private configService = inject(ConfigService);
  private storageService = inject(StorageService);
  private platform = inject(PLATFORM_ID);

  private nativeiOSApp: NativeIosApp;
  private nativeAndroidApp: NativeAndroidApp;
  private reactNativeBridge?: ReactNativeBridge;
  private currentNativePlatform: NativeAppPlatform = NativeAppPlatform.NONE;
  private _appHideStatus$: Subject<boolean> = new Subject();
  private backButtonPressed$: Subject<void> = new Subject();
  private showHeaderHamburgerMenu$: BehaviorSubject<boolean> = new BehaviorSubject(true);

  constructor() {
    const windowRef = this.windowRef;

    let reactNativeRef = windowRef.nativeWindow['ReactNativeWebView'];

    if (!reactNativeRef && this.configService.cfg.enableDevToolPath) {
      const platFormOverride = this.getSessionPlatformOverride();
      if ([NativeAppPlatform.RN_IOS, NativeAppPlatform.RN_ANDROID].includes(platFormOverride)) {
        reactNativeRef = getEmulatedReactNativeBridge(windowRef, platFormOverride);
        windowRef.nativeWindow['ReactNativeWebView'] = reactNativeRef;
        this.setPlatform(platFormOverride);
      }
    }

    if (isPresent(reactNativeRef) && isFunction(reactNativeRef.postMessage)) {
      this.reactNativeBridge = reactNativeRef;
      this.setPlatform(this.detectReactNativePlatform());
    }

    let webkitRef = this.windowRef.nativeWindow['webkit'];
    if (!webkitRef && this.configService.cfg.enableDevToolPath && this.isIOSEmulation) {
      webkitRef = getEmulatedIosRef(this.windowRef);
      this.setPlatform(NativeAppPlatform.IOS);
    }

    let androidRef = this.windowRef.nativeWindow['Android'];

    if (!androidRef && this.configService.cfg.enableDevToolPath && this.isANDROIDEmulation) {
      androidRef = getEmulatedAndroidRef(this.windowRef);
      this.setPlatform(NativeAppPlatform.ANDROID);
    }

    if (isPresent(webkitRef) && isPresent(webkitRef.messageHandlers.isNativeFinnairIosApp)) {
      this.nativeiOSApp = webkitRef.messageHandlers;
      this.setPlatform(NativeAppPlatform.IOS);
    }

    if (isPresent(androidRef)) {
      this.nativeAndroidApp = androidRef;
      this.setPlatform(NativeAppPlatform.ANDROID);
    }

    windowRef.nativeWindow['nativeBridge'] = this;
  }

  init(): void {
    this.makeUiTakeFullAreaInIosWebView();
  }

  /**
   * Passes forward a new hide status
   * @param {boolean} status the next hide status
   */
  setAppHideStatus(status: boolean): void {
    this._appHideStatus$.next(status);
  }

  get appHideStatus(): Observable<boolean> {
    return this._appHideStatus$;
  }

  get isInsideNativeWebView(): boolean {
    const isNativeOverride = this.configService.cfg.enableDevToolPath && this.isNativeEmulation;
    return this.currentNativePlatform !== NativeAppPlatform.NONE || isNativeOverride;
  }

  get isInsideNativeiOSWebView(): boolean {
    const isNativeOverride = this.configService.cfg.enableDevToolPath && this.isIOSEmulation;
    return this.currentNativePlatform === NativeAppPlatform.IOS || isNativeOverride;
  }

  get isInsideNativeAndroidWebView(): boolean {
    const isNativeOverride = this.configService.cfg.enableDevToolPath && this.isANDROIDEmulation;
    return this.currentNativePlatform === NativeAppPlatform.ANDROID || isNativeOverride;
  }

  get isInsideReactNativeWebView(): boolean {
    return (
      this.currentNativePlatform === NativeAppPlatform.RN_IOS ||
      this.currentNativePlatform === NativeAppPlatform.RN_ANDROID
    );
  }

  get nativeAppPlatform(): NativeAppPlatform {
    return this.currentNativePlatform;
  }

  get isLocalView(): boolean {
    return this.isNativeEmulation;
  }

  get backButtonPressed(): Observable<void> {
    return this.backButtonPressed$;
  }

  get isHamburgerMenuVisible(): Observable<boolean> {
    return this.showHeaderHamburgerMenu$;
  }

  urlForAddAJourneyInApp(recloc: string, lastName: string): string {
    return `https://app.finnair.com/addjourney/${recloc}/${lastName}?source=booking`;
  }

  openNativeMenu(): void {
    this.iOSCallIfAccessible(() => this.nativeiOSApp.openMenu)();
    if (this.isAndroidCallAccessible('showMenu')) {
      this.nativeAndroidApp.showMenu();
    }
  }

  addBooking(booking: OrderDataForNative): void {
    this.iOSCallIfAccessible(() => this.nativeiOSApp.addBooking)(booking);
    if (this.isAndroidCallAccessible('addBooking')) {
      this.nativeAndroidApp.addBooking(JSON.stringify(booking));
    }
    this.postReactNativeMessage('addBooking', booking);
  }

  onCheckInLoaded(data: CheckInStatus): void {
    this.iOSCallIfAccessible(() => this.nativeiOSApp.onCheckInLoaded)(data);
    if (this.isAndroidCallAccessible('onCheckInLoaded')) {
      this.nativeAndroidApp.onCheckInLoaded(JSON.stringify(data));
    }
    this.postReactNativeMessage('onCheckInLoaded', data);
  }

  onAcceptCheckIn(data: CheckInResult): void {
    this.iOSCallIfAccessible(() => this.nativeiOSApp.onAcceptCheckIn)(data);
    if (this.isAndroidCallAccessible('onAcceptCheckIn')) {
      this.nativeAndroidApp.onAcceptCheckIn(JSON.stringify(data));
    }
    this.postReactNativeMessage('onAcceptCheckIn', data);
  }

  /**
   * Note: this should be called only after all hooks have been installed and client is ready
   * to receive postMessages from the application.
   */
  clientReady(): void {
    this.iOSCallIfAccessible(() => this.nativeiOSApp.bookingFlowInitialized)();
    if (this.isAndroidCallAccessible('bookingFlowInitialized')) {
      this.nativeAndroidApp.bookingFlowInitialized();
    }
    this.postReactNativeMessage('bookingFlowInitialized');
  }

  /**
   * Passes forward a notion that a mobile device's back button has been pressed
   */
  onBackPressed(): void {
    this.backButtonPressed$.next();
  }

  /**
   * Passes forward a information about hamburger menu visibility
   */
  onShowHamburgerMenu(isOpen: boolean): void {
    this.showHeaderHamburgerMenu$.next(isOpen);
  }

  private iOSCallIfAccessible<T>(target: () => OptionalPostMessageFunc<T>): (T?) => void;
  private iOSCallIfAccessible<T>(target: () => RequiredPostMessageFunc<T>): (T) => void;
  private iOSCallIfAccessible<T>(target: () => PostMessageFunc<T>): (T?) => void {
    if (this.isInsideNativeiOSWebView && isPresent(this.nativeiOSApp) && isPresent(target())) {
      return (msg = '') => target().postMessage(msg);
    }
    return noop;
  }

  private isAndroidCallAccessible(fnName: keyof NativeAndroidApp): boolean {
    return (
      this.isInsideNativeAndroidWebView && isPresent(this.nativeAndroidApp) && isFunction(this.nativeAndroidApp[fnName])
    );
  }

  private makeUiTakeFullAreaInIosWebView(): void {
    // TODO: Remove this when index.template.ejs has viewport-fit always
    if (isPlatformBrowser(this.platform) && this.isInsideNativeiOSWebView) {
      this.meta.updateTag(
        {
          content: 'width=device-width, initial-scale=1, viewport-fit=cover',
        },
        'name="viewport"'
      );
    }
  }

  private get isNativeEmulation(): boolean {
    const override = this.getSessionPlatformOverride();
    return isPresent(override) && override !== NativeAppPlatform.NONE;
  }

  private get isIOSEmulation(): boolean {
    return this.getSessionPlatformOverride() === NativeAppPlatform.IOS;
  }

  private get isANDROIDEmulation(): boolean {
    return this.getSessionPlatformOverride() === NativeAppPlatform.ANDROID;
  }

  private setPlatform(platform: NativeAppPlatform): void {
    this.currentNativePlatform = platform;
  }

  private ensureReactNativeBridge(): void {
    if (this.reactNativeBridge && isFunction(this.reactNativeBridge.postMessage)) {
      return;
    }
    const candidate = this.windowRef.nativeWindow['ReactNativeWebView'];
    if (isPresent(candidate) && isFunction(candidate.postMessage)) {
      this.reactNativeBridge = candidate;
      if (this.currentNativePlatform === NativeAppPlatform.NONE) {
        this.setPlatform(this.detectReactNativePlatform());
      }
    }
  }

  private postReactNativeMessage(event: string, payload?: unknown): void {
    if (!this.isInsideReactNativeWebView) {
      return;
    }
    this.ensureReactNativeBridge();
    if (!this.reactNativeBridge || !isFunction(this.reactNativeBridge.postMessage)) {
      return;
    }
    this.reactNativeBridge.postMessage(JSON.stringify({ event, payload }));
  }

  private getSessionPlatformOverride(): NativeAppPlatform | undefined {
    const rawValue = this.storageService.SESSION.get(NATIVE_KEY);
    if (typeof rawValue !== 'string' || rawValue.length === 0) {
      return undefined;
    }
    return rawValue as NativeAppPlatform;
  }

  private detectReactNativePlatform(): NativeAppPlatform.RN_IOS | NativeAppPlatform.RN_ANDROID {
    if (this.configService.cfg.enableDevToolPath) {
      const override = this.getSessionPlatformOverride();
      if (override === NativeAppPlatform.RN_IOS || override === NativeAppPlatform.RN_ANDROID) {
        return override;
      }
    }
    const nativeWindow = this.windowRef.nativeWindow;
    const webkit = (nativeWindow as { webkit?: { messageHandlers?: Record<string, unknown> } }).webkit;
    const hasIosBridge = webkit?.messageHandlers?.ReactNativeWebView;
    if (hasIosBridge) {
      return NativeAppPlatform.RN_IOS;
    }

    return NativeAppPlatform.RN_ANDROID;
  }
}
